import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  getAuth,
  signOut,
} from "firebase/auth";
import {
  deleteApp,
  initializeApp,
} from "firebase/app";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";

function ManageEmployees() {
  const navigate = useNavigate();

  const availableLanguages = [
    "English",
    "Hindi",
    "Marathi",
    "Gujarati",
    "Tamil",
    "Telugu",
    "Kannada",
    "Malayalam",
    "Bengali",
    "Punjabi",
    "Odia",
    "Assamese",
    "Other",
  ];

  const emptyForm = {
    name: "",
    email: "",
    phone: "",
    status: "Active",
    languages: [],
  };

  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingEmployeeId, setEditingEmployeeId] =
    useState("");

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadEmployees = async () => {
    try {
      setLoading(true);

      const snapshot = await getDocs(
        collection(db, "employees")
      );

      const employeeList = snapshot.docs.map(
        (employeeDocument) => ({
          id: employeeDocument.id,
          ...employeeDocument.data(),
        })
      );

      employeeList.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );

      setEmployees(employeeList);
    } catch (error) {
      console.error(
        "Employee loading error:",
        error
      );

      setMessage(
        "Unable to load employees."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const toggleLanguage = (language) => {
    setFormData((currentData) => {
      const alreadySelected =
        currentData.languages.includes(language);

      return {
        ...currentData,

        languages: alreadySelected
          ? currentData.languages.filter(
              (item) => item !== language
            )
          : [
              ...currentData.languages,
              language,
            ],
      };
    });
  };

  const resetForm = () => {
    setEditingEmployeeId("");
    setFormData(emptyForm);
  };

  const createEmployeeAuthentication =
    async (email) => {
      const secondaryApp = initializeApp(
        auth.app.options,
        `employeeCreator-${Date.now()}`
      );

      try {
        const secondaryAuth =
          getAuth(secondaryApp);

        const userCredential =
          await createUserWithEmailAndPassword(
            secondaryAuth,
            email,
            "Trading@123"
          );

        await signOut(secondaryAuth);

        return userCredential.user.uid;
      } finally {
        await deleteApp(secondaryApp);
      }
    };

  const handleSaveEmployee = async (
    event
  ) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      setMessage(
        "Employee name is required."
      );
      return;
    }

    if (!formData.email.trim()) {
      setMessage(
        "Employee email is required."
      );
      return;
    }

    if (
      !Array.isArray(formData.languages) ||
      formData.languages.length === 0
    ) {
      setMessage(
        "Select at least one language."
      );
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const normalizedEmail =
        formData.email
          .trim()
          .toLowerCase();

      if (editingEmployeeId) {
        await updateDoc(
          doc(
            db,
            "employees",
            editingEmployeeId
          ),
          {
            name: formData.name.trim(),
            email: normalizedEmail,
            phone: formData.phone.trim(),
            status: formData.status,
            languages: formData.languages,

            updatedAt:
              serverTimestamp(),
          }
        );

        setMessage(
          "Employee updated successfully."
        );
      } else {
        const employeeUid =
          await createEmployeeAuthentication(
            normalizedEmail
          );

        await setDoc(
          doc(
            db,
            "employees",
            employeeUid
          ),
          {
            name: formData.name.trim(),
            email: normalizedEmail,
            phone: formData.phone.trim(),
            status: formData.status,
            languages: formData.languages,

            workStatus:
              "Not Started",

            workStartedAt: "",
            workEndedAt: "",

            breakStartedAt: "",
            breakEndedAt: "",

            totalBreakSeconds: 0,
            lastActivityAt: "",

            createdAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),
          }
        );

        setMessage(
          "Employee added successfully. Temporary password: Trading@123"
        );
      }

      resetForm();
      await loadEmployees();
    } catch (error) {
      console.error(
        "Employee save error:",
        error
      );

      if (
        error.code ===
        "auth/email-already-in-use"
      ) {
        setMessage(
          "This email already exists in Firebase Authentication."
        );
      } else {
        setMessage(
          "Unable to save employee."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditEmployee = (
    employee
  ) => {
    setEditingEmployeeId(
      employee.id
    );

    setFormData({
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      status:
        employee.status || "Active",

      languages: Array.isArray(
        employee.languages
      )
        ? employee.languages
        : [],
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDeleteEmployee = async (
    employeeId
  ) => {
    const shouldDelete =
      window.confirm(
        "Delete this employee record?"
      );

    if (!shouldDelete) {
      return;
    }

    try {
      await deleteDoc(
        doc(
          db,
          "employees",
          employeeId
        )
      );

      setMessage(
        "Employee record deleted."
      );

      await loadEmployees();
    } catch (error) {
      console.error(
        "Employee deletion error:",
        error
      );

      setMessage(
        "Unable to delete employee."
      );
    }
  };

  const filteredEmployees =
    useMemo(() => {
      const searchValue =
        searchText
          .trim()
          .toLowerCase();

      if (!searchValue) {
        return employees;
      }

      return employees.filter(
        (employee) =>
          (employee.name || "")
            .toLowerCase()
            .includes(
              searchValue
            ) ||
          (employee.email || "")
            .toLowerCase()
            .includes(
              searchValue
            ) ||
          (employee.phone || "")
            .toLowerCase()
            .includes(
              searchValue
            )
      );
    }, [employees, searchText]);

  return (
    <div className="manage-employees-page">
      <header className="dashboard-header">
        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Manage Employees
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() =>
            navigate(
              "/admin-dashboard"
            )
          }
        >
          Back to Dashboard
        </button>
      </header>

      <main className="dashboard-main">
        <section className="employee-form-card">
          <div className="section-heading">
            <div>
              <p className="dashboard-label">
                EMPLOYEE SETUP
              </p>

              <h2>
                {editingEmployeeId
                  ? "Edit employee"
                  : "Add a new employee"}
              </h2>
            </div>
          </div>

          <form
            onSubmit={
              handleSaveEmployee
            }
          >
            <div className="employee-form-grid">
              <div className="form-group">
                <label>
                  Employee Name *
                </label>

                <input
                  type="text"
                  name="name"
                  value={
                    formData.name
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Employee name"
                />
              </div>

              <div className="form-group">
                <label>
                  Email Address *
                </label>

                <input
                  type="email"
                  name="email"
                  value={
                    formData.email
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="Employee email"
                  disabled={
                    Boolean(
                      editingEmployeeId
                    )
                  }
                />
              </div>

              <div className="form-group">
                <label>
                  Phone Number
                </label>

                <input
                  type="tel"
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder="+91"
                />
              </div>

              <div className="form-group">
                <label>Status</label>

                <select
                  name="status"
                  value={
                    formData.status
                  }
                  onChange={
                    handleInputChange
                  }
                >
                  <option value="Active">
                    Active
                  </option>

                  <option value="Inactive">
                    Inactive
                  </option>
                </select>
              </div>
            </div>

            <div
              style={{
                marginTop: "10px",
                marginBottom: "20px",
                padding: "18px",
                border:
                  "1px solid #bae6fd",
                borderRadius: "16px",
                background:
                  "#f8fcff",
              }}
            >
              <strong>
                Languages Known *
              </strong>

              <p
                style={{
                  marginTop: "5px",
                  marginBottom: "14px",
                  color: "#64748b",
                  fontSize: "13px",
                }}
              >
                Select every language
                this RM can comfortably
                handle.
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {availableLanguages.map(
                  (language) => {
                    const selected =
                      formData.languages.includes(
                        language
                      );

                    return (
                      <label
                        key={
                          language
                        }
                        style={{
                          minHeight:
                            "42px",
                          padding:
                            "0 14px",
                          display:
                            "inline-flex",
                          alignItems:
                            "center",
                          gap: "8px",
                          border:
                            selected
                              ? "1px solid #0284c7"
                              : "1px solid #cbd5e1",
                          borderRadius:
                            "12px",
                          background:
                            selected
                              ? "#e0f2fe"
                              : "#ffffff",
                          color:
                            selected
                              ? "#0369a1"
                              : "#16324f",
                          fontWeight:
                            "700",
                          cursor:
                            "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selected
                          }
                          onChange={() =>
                            toggleLanguage(
                              language
                            )
                          }
                        />

                        {language}
                      </label>
                    );
                  }
                )}
              </div>
            </div>

            {message && (
              <div className="employee-message">
                {message}
              </div>
            )}

            <div className="employee-form-actions">
              <button
                type="submit"
                className="admin-login-button employee-save-button"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingEmployeeId
                    ? "Update Employee"
                    : "Add Employee"}
              </button>

              {editingEmployeeId && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={
                    resetForm
                  }
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <p className="dashboard-label">
                EMPLOYEE DIRECTORY
              </p>

              <h2>
                All Employees
              </h2>
            </div>

            <input
              className="employee-search-input"
              type="text"
              value={
                searchText
              }
              onChange={(
                event
              ) =>
                setSearchText(
                  event.target.value
                )
              }
              placeholder="Search employee"
            />
          </div>

          <div className="employee-table-wrapper">
            <table className="employee-table">
              <thead>
                <tr>
                  <th>
                    Employee
                  </th>

                  <th>
                    Phone
                  </th>

                  <th>
                    Languages
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Work Status
                  </th>

                  <th>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                    >
                      Loading employees...
                    </td>
                  </tr>
                ) : filteredEmployees.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="6"
                    >
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map(
                    (
                      employee
                    ) => (
                      <tr
                        key={
                          employee.id
                        }
                      >
                        <td>
                          <strong>
                            {
                              employee.name
                            }
                          </strong>

                          <small>
                            {
                              employee.email
                            }
                          </small>
                        </td>

                        <td>
                          {employee.phone ||
                            "-"}
                        </td>

                        <td>
                          {Array.isArray(
                            employee.languages
                          ) &&
                          employee
                            .languages
                            .length >
                            0
                            ? employee.languages.join(
                                ", "
                              )
                            : "Not Set"}
                        </td>

                        <td>
                          <span
                            className={
                              employee.status ===
                              "Active"
                                ? "status-badge active"
                                : "status-badge inactive"
                            }
                          >
                            {employee.status ||
                              "Active"}
                          </span>
                        </td>

                        <td>
                          {employee.workStatus ||
                            "Not Started"}
                        </td>

                        <td>
                          <div className="table-action-buttons">
                            <button
                              type="button"
                              className="table-button"
                              onClick={() =>
                                handleEditEmployee(
                                  employee
                                )
                              }
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="table-button danger"
                              onClick={() =>
                                handleDeleteEmployee(
                                  employee.id
                                )
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ManageEmployees;